import { useRef, useState } from 'react';

const CLOSE_DELAY_MS = 200;

/**
 * Coordinates open/close hover state for two mutually-exclusive dropdowns
 * (Industries + Offerings) in the header.
 *
 * - Opening one dropdown immediately closes the other.
 * - Leaving a trigger schedules a delayed close so a fast horizontal cursor
 *   move between trigger and dropdown panel doesn't dismiss it.
 * - Re-entering the dropdown panel cancels the pending close timer.
 *
 * All hover behaviour is disabled on mobile (state must be toggled by click).
 */
export const useDropdownHover = (isMobile) => {
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [offeringsOpen, setOfferingsOpen] = useState(false);
  const closeTimeoutRef = useRef(null);

  const clearPendingClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = (setter) => {
    if (isMobile) return;
    closeTimeoutRef.current = setTimeout(() => setter(false), CLOSE_DELAY_MS);
  };

  const openIndustries = () => {
    if (isMobile) return;
    clearPendingClose();
    setIndustriesOpen(true);
    setOfferingsOpen(false);
  };

  const openOfferings = () => {
    if (isMobile) return;
    clearPendingClose();
    setOfferingsOpen(true);
    setIndustriesOpen(false);
  };

  const closeIndustries = () => scheduleClose(setIndustriesOpen);
  const closeOfferings = () => scheduleClose(setOfferingsOpen);

  const cancelCloseOnDropdownEnter = () => {
    if (!isMobile) clearPendingClose();
  };

  const scheduleCloseOnDropdownLeave = (setter) => scheduleClose(setter);

  return {
    industriesOpen,
    setIndustriesOpen,
    offeringsOpen,
    setOfferingsOpen,
    openIndustries,
    closeIndustries,
    openOfferings,
    closeOfferings,
    cancelCloseOnDropdownEnter,
    scheduleCloseOnDropdownLeave,
  };
};

export default useDropdownHover;
